import { AI_CAPABILITIES, type AiCapability } from '../ai-capability/ai-capability';
import { aiPricingKey } from '../ai-pricing/ai-pricing';

/** The models one provider serves for one capability, in the order they are offered. */
export type AiProviderModels = Readonly<Record<string, readonly string[]>>;

/**
 * The models the platform **curates** — which the integrations catalog turns
 * into the `model` field's options, and which the `ai.pricing` rate table is
 * keyed by.
 *
 * One list, two consumers, so the two cannot drift: a model an operator can
 * choose is a model that has a rate (asserted in the api's settings-registry
 * spec), and a rate row names a model that exists.
 *
 * **Curation is guidance, not a gate.** The `select` field type deliberately
 * accepts a value outside this list, because a vendor ships a model faster than
 * a deployment ships a release — and because removing one from here must never
 * make an existing configuration unreadable. Adding a model is one line here
 * plus one rate row in `ai/pricing.definition.ts`; forgetting the rate is a
 * failing spec, not a surprise on the invoice.
 */
export const AI_MODEL_CATALOGUE: Readonly<Record<AiCapability, AiProviderModels>> = {
  'ai-text': {
    // Ordered most- to least-capable: the panel offers them in this order, and
    // the head of the list is the one an operator picks without reading.
    anthropic: ['claude-opus-5', 'claude-sonnet-5', 'claude-haiku-4-5'],
    gemini: ['gemini-2.5-pro', 'gemini-2.5-flash'],
  },
  'ai-embedding': {
    // Anthropic serves no embedding model; the catalog declares no such
    // provider for this capability either, and the spec holds the two together.
    gemini: ['gemini-embedding-001'],
  },
  'face-embedding': {
    // Gemini Embedding 2 is natively multimodal — one vector space for text and
    // images — and is reachable from the Gemini Developer API with the same
    // plain API key the other two capabilities use.
    gemini: ['gemini-embedding-2'],
  },
  'ai-image': {
    // Both text providers' current models are vision-capable, so the photo
    // advisory offers the same families as `ai-text`, fastest first: a
    // per-upload classification is a volume call, not a reasoning one.
    gemini: ['gemini-2.5-flash', 'gemini-2.5-pro'],
    anthropic: ['claude-haiku-4-5', 'claude-sonnet-5', 'claude-opus-5'],
  },
};

/**
 * The curated models for one provider — **empty**, never a throw, when this
 * capability does not curate that provider.
 *
 * The integrations catalog decides which providers exist; this decides which
 * models each is offered. A provider that gains models later must not make the
 * descriptor that reads this explode in the meantime.
 */
export function aiModelsFor(capability: AiCapability, provider: string): readonly string[] {
  return AI_MODEL_CATALOGUE[capability][provider] ?? [];
}

/**
 * Every catalogued model as an `ai.pricing` key — the rate table's row list,
 * and what the drift spec checks the coded defaults against.
 */
export function aiPricingKeysForCatalogue(): readonly string[] {
  return AI_CAPABILITIES.flatMap((capability) =>
    Object.entries(AI_MODEL_CATALOGUE[capability]).flatMap(([provider, models]) =>
      models.map((model) => aiPricingKey(capability, provider, model)),
    ),
  );
}
